import Link from 'next/link';
import FutureImage from '../FutureImage';
import laosBoardwalk from '../../public/img/landing/laos-boardwalk.jpg';
import { fraunces } from './fonts';
import {
  Accent,
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PlaneIcon,
  Stamp,
} from './ui';

const FEATURED_USERS = [
  'mibreit-photo',
  'x-rain',
  'for91days',
  'cocuyeras',
  'koenau',
  'juancar347',
  'adambarratt',
];

export default function Hero({ onJoin, userCount }) {
  const bloggerCount = userCount
    ? Math.floor(userCount / 1000) * 1000
    : 4000;

  // Entrance animations are pure CSS (.tf-rise* below) so they play at first
  // paint — framer-motion here would serialize opacity:0 into the SSR HTML
  // and keep the hero (the LCP element) invisible until hydration.
  const rise = i => ({ animationDelay: `${0.08 * i}s` });

  return (
    <section className="tw-relative tw-overflow-hidden tw-pt-28 tw-pb-16 sm:tw-pt-32 lg:tw-pt-36 lg:tw-pb-24">
      {/* Atmosphere: teal glow + faint contour lines. Pre-blurred gradient —
          a filter: blur() this size wrecks scroll performance on Safari. */}
      <div
        aria-hidden="true"
        className="tf-hero-glow tw-pointer-events-none tw-absolute -tw-top-40 -tw-right-40 tw-h-[600px] tw-w-[600px]"
      />
      <svg
        aria-hidden="true"
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-text-[#1D2D28] tw-opacity-[0.045] dark:tw-text-[#EBE6D9] dark:tw-opacity-[0.05]"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 1200 700"
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M-60 120c180-70 360 60 540-10s320-90 540-20 240 80 240 80" />
          <path d="M-60 180c180-70 360 60 540-10s320-90 540-20 240 80 240 80" />
          <path d="M-60 540c200-90 380 70 560 0s300-110 520-30 240 90 240 90" />
          <path d="M-60 600c200-90 380 70 560 0s300-110 520-30 240 90 240 90" />
        </g>
      </svg>

      <div className="tw-relative tw-mx-auto tw-grid tw-max-w-7xl tw-grid-cols-1 tw-items-center tw-gap-14 tw-px-4 sm:tw-px-6 lg:tw-grid-cols-[1.05fr_0.95fr] lg:tw-gap-8 lg:tw-px-8">
        {/* Copy column */}
        <div>
          <div className="tf-rise" style={rise(0)}>
            <Eyebrow>Now boarding · travel bloggers</Eyebrow>
          </div>
          <h1
            style={rise(1)}
            className={`tf-rise ${fraunces.className} ${INK} tw-m-0 tw-text-[2.7rem] tw-font-semibold tw-leading-[1.06] tw-tracking-tight sm:tw-text-6xl lg:tw-text-[4.2rem]`}
          >
            The travel blog you’ve been <Accent>meaning</Accent> to start.
          </h1>
          <p
            style={rise(2)}
            className={`tf-rise ${MUTED} tw-mt-6 tw-mb-0 tw-max-w-xl tw-text-lg tw-leading-relaxed sm:tw-text-xl`}
          >
            TravelFeed gives you a beautiful, lightning-fast blog on your own
            domain, where maps and destination pages build themselves as you
            write, readers arrive from day one, and earning from your
            adventures is built in. No plugins, no hosting headaches. Just
            write.
          </p>
          <div
            style={rise(3)}
            className="tf-rise tw-mt-9 tw-flex tw-flex-wrap tw-items-center tw-gap-4"
          >
            <CtaButton onClick={onJoin}>
              Start your travel blog
              <ArrowIcon />
            </CtaButton>
            <GhostButton href="#pricing">See plans</GhostButton>
          </div>
          <p
            style={rise(4)}
            className={`tf-rise ${MUTED} tw-mt-5 tw-mb-0 tw-font-mono tw-text-xs tw-tracking-wide`}
          >
            From $19/year · Online in minutes · Cancel anytime
          </p>
          <div
            style={rise(5)}
            className="tf-rise tw-mt-10 tw-flex tw-flex-wrap tw-items-center tw-gap-4"
          >
            <div className="tw-flex -tw-space-x-2.5">
              {FEATURED_USERS.map(user => (
                <Link
                  href={`/@${user}`}
                  prefetch={false}
                  key={user}
                  className="tw-block tw-rounded-full"
                >
                  <FutureImage
                    src={`/avatar/${user}/small`}
                    alt={`@${user} on TravelFeed`}
                    width={40}
                    height={40}
                    unoptimized
                    className="tw-h-10 tw-w-10 tw-rounded-full tw-border-2 tw-border-solid tw-border-[#FAF8F1] dark:tw-border-[#0E1A16] tw-object-cover tw-transition-transform hover:-tw-translate-y-1"
                  />
                </Link>
              ))}
            </div>
            <p className={`${MUTED} tw-m-0 tw-text-sm tw-leading-snug`}>
              <strong className={`${INK} tw-font-semibold`}>
                {bloggerCount.toLocaleString('en-US')}+ travel bloggers
              </strong>
              <br />
              already write here
            </p>
          </div>
        </div>

        {/* Postcard collage */}
        <div className="tf-rise-card tw-relative tw-mx-auto tw-w-full tw-max-w-[480px] lg:tw-max-w-none">
          {/* Dashed flight route behind the postcard */}
          <svg
            aria-hidden="true"
            className="tw-absolute -tw-left-24 -tw-top-14 tw-hidden tw-h-40 tw-w-72 tw-text-primary-600/50 dark:tw-text-primary-300/40 lg:tw-block"
            viewBox="0 0 280 160"
            fill="none"
          >
            <path
              d="M6 150 C 80 120, 120 40, 262 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="3 8"
              strokeLinecap="round"
            />
            <circle cx="6" cy="150" r="4" fill="currentColor" />
          </svg>
          <PlaneIcon className="tw-absolute tw-right-[88%] tw-top-[-9%] tw-hidden tw-h-6 tw-w-6 -tw-rotate-12 tw-text-primary-600 dark:tw-text-primary-300 lg:tw-block" />

          {/* Boarding-pass tag peeking out behind */}
          <div
            aria-hidden="true"
            className="tw-absolute -tw-left-5 -tw-top-7 tw-rotate-[-7deg] tw-rounded-lg tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF6] dark:tw-bg-[#1A2A24] tw-px-4 tw-py-2.5 tw-font-mono tw-text-[10px] tw-tracking-[0.2em] tw-text-[#5F6F69] dark:tw-text-[#93A69E] tw-shadow-md"
          >
            TF·001 — ONE WAY ✈ NO RETURN
          </div>

          {/* The postcard */}
          <figure className="tw-relative tw-m-0 tw-rotate-[-2.5deg] tw-rounded-md tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-pb-12 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)] tw-transition-transform tw-duration-500 hover:tw-rotate-[-1deg]">
            <div className="tw-relative tw-aspect-[4/3] tw-overflow-hidden tw-rounded-sm">
              <FutureImage
                src={laosBoardwalk}
                alt="A wooden boardwalk winding through rice paddies toward karst mountains in Vang Vieng, Laos"
                fill
                sizes="(max-width: 992px) 90vw, 45vw"
                className="tw-object-cover"
                priority
                fetchPriority="high"
              />
            </div>
            <figcaption className="tw-flex tw-items-baseline tw-justify-between tw-px-1 tw-pt-3 tw-font-mono tw-text-[10px] tw-tracking-[0.22em] tw-text-[#5F6F69] [&::after]:tw-hidden">
              <span>VANG VIENG · LAOS</span>
              <span className="tw-hidden sm:tw-inline">FIELD NOTES Nº 047</span>
            </figcaption>
            {/* Passport stamp overlapping the frame */}
            <Stamp className="tw-absolute -tw-right-7 -tw-top-8 tw-h-24 tw-w-24 tw-rotate-12 tw-text-amber-600/90 dark:tw-text-amber-600" />
          </figure>

          {/* Floating domain chip — the product promise */}
          <div className="tf-rise-chip tw-absolute -tw-bottom-6 -tw-left-3 sm:-tw-left-8 tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-px-4 tw-py-3 tw-shadow-[0_18px_40px_-16px_rgba(29,45,40,0.4)]">
            <span className="tw-relative tw-flex tw-h-2.5 tw-w-2.5">
              <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping motion-reduce:tw-animate-none tw-rounded-full tw-bg-primary-400 tw-opacity-60" />
              <span className="tw-relative tw-inline-flex tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-primary-500" />
            </span>
            <span className={`${INK} tw-font-mono tw-text-sm`}>
              www.youradventures.com
            </span>
            <span className="tw-rounded-full tw-bg-primary-600/10 dark:tw-bg-primary-300/20 tw-px-2.5 tw-py-1 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-widest tw-text-primary-700 dark:tw-text-primary-300">
              LIVE
            </span>
          </div>
        </div>
      </div>
      <style jsx global>{`
        /* Plain CSS: tw-bg-[radial-gradient(...)] arbitrary values silently
           compile to nothing in this repo's Tailwind 3.1.8. */
        .tf-hero-glow {
          background: radial-gradient(
            closest-side,
            rgba(38, 166, 154, 0.16),
            rgba(38, 166, 154, 0)
          );
        }
        .tw-dark .tf-hero-glow {
          background: radial-gradient(
            closest-side,
            rgba(0, 150, 136, 0.14),
            rgba(0, 150, 136, 0)
          );
        }
        @keyframes tf-rise {
          from {
            opacity: 0;
            transform: translateY(26px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes tf-rise-card {
          from {
            opacity: 0;
            transform: translateY(36px) rotate(1deg);
          }
          to {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
        }
        .tf-rise {
          animation: tf-rise 0.7s cubic-bezier(0.21, 0.65, 0.36, 1) both;
        }
        .tf-rise-card {
          animation: tf-rise-card 0.9s cubic-bezier(0.21, 0.65, 0.36, 1) 0.25s
            both;
        }
        .tf-rise-chip {
          animation: tf-rise 0.6s ease-out 0.85s both;
        }
        @media (prefers-reduced-motion: reduce) {
          .tf-rise,
          .tf-rise-card,
          .tf-rise-chip {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
